import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from 'react';
import "./App.css";
import BannerAudioPage from "./pages/demoCreator";
import UserLogIn from "./pages/UserLogIn";
import { getToken } from "./utils/auth"
import MarketingAgent from "./pages/MarketingAgent";
import MIA from "./pages/new_mia"
import UserManagement from "./pages/UserManagement";
import { RefreshCw, AudioLines, TextSelect, Monitor, Smartphone } from 'lucide-react';


const AssetCard = ({
  id,
  title,
  children,
  spinning,
  handleRegenerate,
  aspectRatio,
  setAspectRatio,
  setFetchAudioTextStatus,
  setFetchonlyAudioStatus,
}) => (
  <div className="bg-blue-800 p-4 rounded-lg border border-blue-700 flex flex-col">
    <div className="flex items-center justify-between mb-3">
      
      {/* Title + ratio toggles */}
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-blue-400"></span>
        <span className="text-sm font-bold text-blue-100">{title}</span>

        {/* Ratio toggle (not for audio) */}
        {id !== "audio" && (
          <div className="flex items-center gap-1 ml-2">
            {/* 16:9 */}
            <button
              onClick={() => setAspectRatio("16:9")}
              className={`p-1 rounded transition-colors ${
                aspectRatio === "16:9"
                  ? "bg-blue-600 text-white"
                  : "text-blue-300 hover:bg-blue-700"
              }`}
              title="16:9"
            >
              <Monitor size={14} />
            </button>

            {/* 9:16 */}
            <button
              onClick={() => setAspectRatio("9:16")}
              className={`p-1 rounded transition-colors ${
                aspectRatio === "9:16"
                  ? "bg-blue-600 text-white"
                  : "text-blue-300 hover:bg-blue-700"
              }`}
              title="9:16"
            >
              <Smartphone size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1">
        {id === "audio" ? (
            <>
              <button
                disabled={spinning["audio"] || spinning["audio_text"] || spinning["audio_only"]}
                onClick={() => setFetchAudioTextStatus?.(true)}
                className="text-blue-300 hover:text-white p-1 rounded hover:bg-blue-700 disabled:opacity-50"
                title="Regenerate Text"
              >
                
                <TextSelect size={16} className={spinning["audio_text"] ? "animate-spin" : ""} />
              </button>
  
              <button
                disabled={spinning["audio"] || spinning["audio_text"] || spinning["audio_only"]}
                onClick={() => setFetchonlyAudioStatus?.(true)}
                className="text-blue-300 hover:text-white p-1 rounded hover:bg-blue-700 disabled:opacity-50"
                title="Regenerate Audio"
              >
                <AudioLines size={16} className={spinning["audio_only"] ? "animate-spin" : ""} />
              </button>
  
              <button
                disabled={spinning["audio"] || spinning["audio_text"] || spinning["audio_only"]}
                onClick={() => handleRegenerate(id)}
                className="text-blue-300 hover:text-white p-1 rounded hover:bg-blue-700 disabled:opacity-50"
                title="Regenerate"
              >
                <RefreshCw size={16} className={spinning[id] ? "animate-spin" : ""} />
              </button>
            </>
              ) : (
          <button
            disabled={spinning[id]}
            onClick={() => handleRegenerate(id)}
            className="text-blue-300 hover:text-white p-1 rounded hover:bg-blue-700 disabled:opacity-50"
            title="Regenerate"
          >
            <RefreshCw size={16} className={spinning[id] ? "animate-spin" : ""} />
          </button>
        )}
      </div>
    </div>

    <div className="flex-1">{children}</div>
  </div>
);

// Custom higher-order component (HOC) for route authentication
const PrivateRoute = ({ children }) => {
  if (!getToken()) return <Navigate to="/" />;
  return children;
};

function App() {
  const [current_balance, setCurrentBalance] = useState('Loading...');

  const [role, setRole] = useState(null);
  
  const [audioUrl, setAudioUrl] = useState('');
  const [audioText, setAudioText] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');

  const [allGenratedBanners, setAllGenratedBanners] = useState([])
  const [allGenratedVideos, setAllGenratedVideos] = useState([])
  const [allGenratedAudios, setAllGenratedAudios] = useState([])
  const [allUploadedAudios, setAllUploadedAudios] = useState([])

  const [fetchAudioTextStatus, setFetchAudioTextStatus] = useState(false)
  const [fetchonlyAudioStatus, setFetchonlyAudioStatus] = useState(false)

  //step2 generate states
  const [spinning, setSpinning] = useState({});
  const [animateBanner, setAnimateBanner] = useState(false);
  const [aspectBannerRatio, setAspectBannerRatio] = useState("16:9");
  const [aspectVideoRatio, setAspectVideoRatio] = useState("16:9");
  const [templateBanner, setTemplateBanner] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const onStorage = (e) => {
      // Only react to auth-related localStorage changes from *other* tabs.
      if (e?.storageArea && e.storageArea !== localStorage) return;

      const key = e?.key; // null when localStorage.clear() is called
      const isAuthKey = key === null || key === "authToken" || key === "tokenExpiry";
      if (!isAuthKey) return;

      // Token refresh flows sometimes remove then immediately re-set.
      // Delay slightly and re-check before redirecting to avoid loops across tabs.
      window.setTimeout(() => {
        if (!getToken() && window.location.pathname !== "/") {
          window.location.assign("/");
        }
      }, 75);
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route index element={
          <UserLogIn 
        />} />
        <Route
          path="/CatchAudioDisplay/:retailer_id/:branch_id"
          element={
            <PrivateRoute>
              <BannerAudioPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/MarketingAgent/:screen_id"
          element={
            <PrivateRoute>
              <MIA 
                current_balance={current_balance}
                setCurrentBalance={setCurrentBalance}
                role={role}
                setRole={setRole}
                audioUrl={audioUrl}
                setAudioUrl={setAudioUrl}
                audioText={audioText}
                setAudioText={setAudioText}
                bannerUrl={bannerUrl}
                setBannerUrl={setBannerUrl}
                videoUrl={videoUrl}
                setVideoUrl={setVideoUrl}
                animateBanner={animateBanner}
                setAnimateBanner={setAnimateBanner}
                aspectBannerRatio={aspectBannerRatio}
                setAspectBannerRatio={setAspectBannerRatio}
                aspectVideoRatio={aspectVideoRatio}
                setAspectVideoRatio={setAspectVideoRatio}
                templateBanner={templateBanner}
                setTemplateBanner={setTemplateBanner}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                AssetCard={AssetCard}
                spinning={spinning}
                setSpinning={setSpinning}
                fetchAudioTextStatus={fetchAudioTextStatus}
                setFetchAudioTextStatus={setFetchAudioTextStatus}
                fetchonlyAudioStatus={fetchonlyAudioStatus}
                setFetchonlyAudioStatus={setFetchonlyAudioStatus}
                allGenratedBanners={allGenratedBanners}
                setAllGenratedBanners={setAllGenratedBanners}
                allGenratedVideos={allGenratedVideos}
                setAllGenratedVideos={setAllGenratedVideos}
                allGenratedAudios={allGenratedAudios}
                setAllGenratedAudios={setAllGenratedAudios}
                allUploadedAudios={allUploadedAudios}
                setAllUploadedAudios={setAllUploadedAudios}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/MarketingAgent"
          element={
            <PrivateRoute>
              <MIA 
                current_balance={current_balance}
                setCurrentBalance={setCurrentBalance}
                role={role}
                setRole={setRole}
                audioUrl={audioUrl}
                setAudioUrl={setAudioUrl}
                audioText={audioText}
                setAudioText={setAudioText}
                bannerUrl={bannerUrl}
                setBannerUrl={setBannerUrl}
                videoUrl={videoUrl}
                setVideoUrl={setVideoUrl}
                animateBanner={animateBanner}
                setAnimateBanner={setAnimateBanner}
                aspectBannerRatio={aspectBannerRatio}
                setAspectBannerRatio={setAspectBannerRatio}
                aspectVideoRatio={aspectVideoRatio}
                setAspectVideoRatio={setAspectVideoRatio}
                templateBanner={templateBanner}
                setTemplateBanner={setTemplateBanner}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                AssetCard={AssetCard}
                spinning={spinning}
                setSpinning={setSpinning}
                fetchAudioTextStatus={fetchAudioTextStatus}
                setFetchAudioTextStatus={setFetchAudioTextStatus}
                fetchonlyAudioStatus={fetchonlyAudioStatus}
                setFetchonlyAudioStatus={setFetchonlyAudioStatus}
                allGenratedBanners={allGenratedBanners}
                setAllGenratedBanners={setAllGenratedBanners}
                allGenratedVideos={allGenratedVideos}
                setAllGenratedVideos={setAllGenratedVideos}
                allGenratedAudios={allGenratedAudios}
                setAllGenratedAudios={setAllGenratedAudios}
                allUploadedAudios={allUploadedAudios}
                setAllUploadedAudios={setAllUploadedAudios}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/UserManagement"
          element={
            <PrivateRoute>
              <UserManagement
              />
            </PrivateRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
